import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GridOnIcon from '@mui/icons-material/GridOn';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import ModalConfirmation from './ModalConfirmation';

const QTY_OPTIONS = [1, 2, 3, 5, 10];

/** One icon per mechanic, so the two Mystery prices still read as one family. */
const MECHANIC_ICON = {
  'match-three': AutoAwesomeIcon,
  'grid-bingo': GridOnIcon,
};

function FlaxScreen() {
  const { t } = useTranslation();
  const {
    updateIsSellerOpen,
    updateIsFlaxOpen,
    updateIsLoadingScreen,
    updateTransactions,
    hall,
    printer,
    operator,
    session,
    userInfo,
    parentInfo,
  } = useContext(AppContext);

  const { socket } = useContext(SocketContext);

  const [games, setGames] = useState([]);
  const [gameID, setGameID] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [sold, setSold] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  // Refs so the socket handlers, bound once, always read current values.
  const hallRef = useRef(hall);
  useEffect(() => {
    hallRef.current = hall;
  }, [hall]);

  const printerRef = useRef(printer);
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);

  const operatorRef = useRef(operator);
  useEffect(() => {
    operatorRef.current = operator;
  }, [operator]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const parentInfoRef = useRef(parentInfo);
  useEffect(() => {
    parentInfoRef.current = parentInfo;
  }, [parentInfo]);

  // Set before emitting so the sale handler knows whether to close afterwards.
  const closeAfterSaleRef = useRef(false);
  const handleCloseRef = useRef(null);

  const updateTransactionsRef = useRef(updateTransactions);
  useEffect(() => {
    updateTransactionsRef.current = updateTransactions;
  }, [updateTransactions]);

  const selectedGame = games.find((g) => g.gameID === gameID) || null;
  const totalKr = selectedGame ? (selectedGame.priceMinor / 100) * quantity : 0;
  const canConfirm = Boolean(selectedGame) && quantity > 0 && !isRequesting;

  /* ---------- socket ---------- */
  useEffect(() => {
    updateIsLoadingScreen(true);
    socket.emit('getFlaxGames', '');

    const handleGames = (data) => {
      updateIsLoadingScreen(false);
      if (!data || !data.length) {
        toast.error(t('error.error'));
        return;
      }
      setGames(data);
    };

    const handleSale = (data) => {
      updateIsLoadingScreen(false);
      setIsRequesting(false);

      if (!data || !data.ok) {
        closeAfterSaleRef.current = false;
        toast.error(
          data && data.exhausted ? 'Ingen flere lodd igjen' : t('error.error')
        );
        return;
      }

      setSold((prev) => [...data.tickets, ...prev]);

      updateTransactionsRef.current({
        name: `Ny Skrapelodd - ${data.title} (x${data.sold})`,
        amount: parseInt(data.totalMinor / 100, 10),
        type: 'transaction',
      });

      // Partial sale: the pool ran dry mid-batch, so say what actually printed.
      if (data.exhausted) {
        toast.error(`Kun ${data.sold} lodd igjen — resten ble ikke solgt`);
      }
      setQuantity(0);

      if (closeAfterSaleRef.current) {
        closeAfterSaleRef.current = false;
        if (handleCloseRef.current) handleCloseRef.current();
      }
    };

    socket.on('getFlaxGames', handleGames);
    socket.on('newSaleFlax', handleSale);
    return () => {
      socket.off('getFlaxGames', handleGames);
      socket.off('newSaleFlax', handleSale);
    };
  }, [socket]);

  /* ---------- handlers ---------- */
  const requestConfirm = (closeAfter) => {
    closeAfterSaleRef.current = closeAfter;
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    setIsRequesting(true);
    updateIsLoadingScreen(true);
    // Parent account takes precedence over the user, same as the papir sales.
    const parent = parentInfoRef.current;
    const user = userInfoRef.current;

    socket.emit('newSaleFlax', {
      gameID,
      quantity,
      printer: printerRef.current,
      bingoName: hallRef.current,
      operator: operatorRef.current,
      sessionID: sessionRef.current,
      userID: (parent && parent.id) || (user && user.id) || 0,
      user7Pincode:
        (parent && parent['7 digit PIN']) ||
        (user && user['7 digit PIN']) ||
        'Gjest',
    });
  };

  const handleClose = () => {
    setGameID('');
    setQuantity(0);
    setSold([]);
    updateIsFlaxOpen(false);
    updateIsSellerOpen(false);
  };
  handleCloseRef.current = handleClose;

  /* ---------- render ---------- */
  return (
    <Grid
      item
      xs={12}
      sx={{
        height: '100vh',
        p: 3,
        borderRadius: 4,
        background: 'transparent',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="h4"
        align="left"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <PrintIcon sx={{ fontSize: 40 }} />
        Skrapelodd
      </Typography>

      {/* type — the hero of the screen, so it takes the free vertical space */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography sx={labelSx}>Type</Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            width: '100%',
            flex: 1,
            minHeight: 180,
            maxHeight: 380,
          }}
        >
          {games.map((g) => {
            const active = gameID === g.gameID;
            const Icon = MECHANIC_ICON[g.mechanic] || ConfirmationNumberIcon;
            return (
              <Button
                key={g.gameID}
                onClick={() => setGameID(g.gameID)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  borderRadius: '16px',
                  textTransform: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 1,
                  color: active ? '#000' : '#fff',
                  bgcolor: active ? '#e3a406' : 'rgba(255,255,255,0.08)',
                  border: active
                    ? '2px solid #e3a406'
                    : '2px solid rgba(255,255,255,0.14)',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: active ? '#f0b41f' : 'rgba(255,255,255,0.16)',
                    boxShadow: 'none',
                  },
                }}
              >
                <Icon sx={{ fontSize: 64, opacity: active ? 0.9 : 0.75 }} />
                <span style={{ fontWeight: 900, fontSize: '28px' }}>
                  {g.title}
                </span>
                <span style={{ fontSize: '16px', opacity: 0.8 }}>
                  {g.subtitle}
                </span>
                <span
                  style={{
                    marginTop: '4px',
                    padding: '4px 18px',
                    borderRadius: '999px',
                    fontWeight: 900,
                    fontSize: '22px',
                    border: active
                      ? '2px solid rgba(0,0,0,0.35)'
                      : '2px solid rgba(255,255,255,0.25)',
                  }}
                >
                  {g.priceMinor / 100} kr
                </span>
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* quantity */}
      <Box sx={{ mt: 2 }}>
        <Typography sx={labelSx}>{t('sales.cards')}</Typography>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          {QTY_OPTIONS.map((n) => {
            const active = quantity === n;
            return (
              <Button
                key={n}
                onClick={() => setQuantity(n)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 64,
                  borderRadius: '12px',
                  fontWeight: 900,
                  fontSize: '22px',
                  textTransform: 'none',
                  color: active ? '#000' : '#fff',
                  bgcolor: active ? '#FFC727' : 'rgba(255,255,255,0.08)',
                  border: active
                    ? '2px solid #FFC727'
                    : '2px solid rgba(255,255,255,0.14)',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: active ? '#FFD54F' : 'rgba(255,255,255,0.16)',
                    boxShadow: 'none',
                  },
                }}
              >
                x{n}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* total */}
      <Typography
        align="center"
        sx={{ mt: 2, fontSize: '1.6em', fontWeight: 900 }}
      >
        {selectedGame
          ? `${selectedGame.title} x${quantity} = ${totalKr} kr`
          : 'Velg type og antall'}
      </Typography>

      {/* printed this session — capped, so it never squeezes the type cards */}
      {sold.length > 0 && (
        <Box sx={{ mt: 2, maxHeight: 120, overflowY: 'auto', ...scrollSx }}>
          <Typography sx={{ ...labelSx, mb: 1 }}>
            Skrevet ut ({sold.length})
          </Typography>
          <Grid container spacing={1}>
            {sold.map((s) => (
              <Grid item xs={3} key={s.token}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.14)',
                    bgcolor: 'rgba(255,255,255,0.05)',
                  }}
                >
                  <div style={{ fontWeight: 900 }}>#{s.serial}</div>
                  <div style={{ fontSize: '13px', opacity: 0.75 }}>
                    {s.priceMinor / 100} kr
                  </div>
                  {!s.printed && (
                    <div style={{ fontSize: '13px', color: '#ff8a80' }}>
                      Ikke skrevet ut
                    </div>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* actions */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 2,
          justifyContent: 'center',
        }}
      >
        <button
          className="btn-custom-postpone"
          onClick={handleClose}
          disabled={isRequesting}
          style={btnSx(!isRequesting)}
        >
          <ArrowBackIcon style={{ fontSize: '20px' }} />
          <span style={{ fontSize: '15px' }}>
            {t('sales.backToMainScreen')}
          </span>
        </button>

        <button
          className="btn-custom-yes-and-stay"
          onClick={() => requestConfirm(false)}
          disabled={!canConfirm}
          style={btnSx(canConfirm)}
        >
          <AddCircleIcon style={{ fontSize: '20px' }} />
          <span style={{ fontSize: '15px' }}>
            {t('sales.confirmAndContinue')}
          </span>
        </button>

        <button
          className="btn-custom-yes"
          onClick={() => requestConfirm(true)}
          disabled={!canConfirm}
          style={btnSx(canConfirm)}
        >
          <PrintIcon style={{ fontSize: '20px' }} />
          <span style={{ fontSize: '15px' }}>{t('sales.confirmAndClose')}</span>
        </button>
      </Box>

      {showConfirm && selectedGame && (
        <ModalConfirmation
          game={selectedGame}
          quantity={quantity}
          totalKr={totalKr}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </Grid>
  );
}

// Same treatment as ModalPapirGameBatchResult, in the screen's own yellow.
const scrollSx = {
  overscrollBehavior: 'contain',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255,255,255,0.06)',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#FFC727',
    borderRadius: '8px',
    '&:hover': { background: '#FFD54F' },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: '#FFC727 rgba(255,255,255,0.06)',
};

const btnSx = (enabled) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  whiteSpace: 'nowrap',
  opacity: enabled ? 1 : 0.4,
  cursor: enabled ? 'pointer' : 'not-allowed',
});

const labelSx = {
  color: '#FFC727',
  fontWeight: 900,
  fontSize: '14px',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  mb: 1,
};

export default FlaxScreen;
